import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { SectionProps } from '../../utils/SectionProps';
import SectionHeader from './partials/SectionHeader';
import Input from '../elements/Input';
import Button from '../elements/Button';
import FormLabel from '../elements/FormLabel';
import FormHint from '../elements/FormHint';
import Radio from '../elements/Radio';
import { submitQvFeedback } from '../../api/qvFeedback';
import { AuthContext } from '../../context/AuthContext';

const propTypes = {
  ...SectionProps.types
};

const defaultProps = {
  ...SectionProps.defaults
};

const PAGE_STATES = {
  ready: 'ready',
  submitting: 'submitting',
  success: 'success'
};

const REGISTRATION_OPTIONS = [
  { value: '1', label: 'Very difficult' },
  { value: '2', label: 'Difficult' },
  { value: '3', label: 'Neutral' },
  { value: '4', label: 'Easy' },
  { value: '5', label: 'Very easy' }
];

const INSTALLATION_OPTIONS = [
  { value: '1', label: 'Very difficult' },
  { value: '2', label: 'Difficult' },
  { value: '3', label: 'Neutral' },
  { value: '4', label: 'Easy' },
  { value: '5', label: 'Very easy' }
];

const CHART_USEFULNESS_OPTIONS = [
  { value: '1', label: 'Not useful or clear' },
  { value: '2', label: 'Slightly useful' },
  { value: '3', label: 'Mixed' },
  { value: '4', label: 'Useful' },
  { value: '5', label: 'Very useful and clear' }
];

const ANSWER_SPEED_OPTIONS = [
  { value: '1', label: 'Way too slow' },
  { value: '2', label: 'Slow' },
  { value: '3', label: 'Acceptable' },
  { value: '4', label: 'Fast' },
  { value: '5', label: 'Very fast' }
];

const COMPARISON_OPTIONS = [
  { value: '1', label: 'Much worse' },
  { value: '2', label: 'Worse' },
  { value: '3', label: 'About the same' },
  { value: '4', label: 'Better' },
  { value: '5', label: 'Much better' }
];

const LIKELIHOOD_OPTIONS = [
  { value: '1', label: 'Very unlikely' },
  { value: '2', label: 'Unlikely' },
  { value: '3', label: 'Unsure' },
  { value: '4', label: 'Likely' },
  { value: '5', label: 'Very likely' }
];

const WAIT_FEELING_OPTIONS = [
  { value: '1', label: 'Very slow' },
  { value: '2', label: 'Slow' },
  { value: '3', label: 'Neutral' },
  { value: '4', label: 'Fast' },
  { value: '5', label: 'Very Fast' }
];

const SENSIBLE_ANSWER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'most', label: 'Most' },
  { value: 'about_half', label: 'About half' },
  { value: 'few', label: 'Few' },
  { value: 'none', label: 'None' }
];

const SQL_REVIEW_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not_confident', label: 'I did not feel confident judging the SQL' },
  { value: 'not_reviewed', label: 'I did not review the SQL' }
];

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

const PAYMENT_LIKELIHOOD_SCALE_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' }
];

const PAYMENT_LIKELIHOOD_FEATURES = [
  {
    key: 'conversational_context',
    title: '1. Conversational context',
    description: 'The system remembers previously asked questions so users can converse with the database rather than asking single questions.'
  },
  {
    key: 'automated_metric_analysis',
    title: '2. Automated Metric Analysis',
    description: 'The system automatically extracts all important metrics and analyses them to highlight potential growth and areas of concern.'
  },
  {
    key: 'additional_plugins',
    title: '3. Additional plugins',
    description: 'The system extracts data from other wordpress / woocommerce plugins in addition to the default tables.'
  },
  {
    key: 'more_than_10_questions_per_month',
    title: '4. More than 10 questions per month',
    description: '100 questions or more.'
  },
  {
    key: 'scheduled_reports_and_alerts',
    title: '5. Scheduled reports & smart alerts',
    description: 'Automated weekly or daily digests delivered to your inbox, plus instant notifications when something meaningful changes'
  },
  {
    key: 'forecasting_and_predictive_insights',
    title: '6. Forecasting & predictive insights',
    description: 'Predict next month\'s revenue, project when popular products will run out of stock, and spot trending categories before they peak.'
  },
  {
    key: 'team_access_with_permissions',
    title: '7. Team access with role-based permissions',
    description: 'Invite team members (accountant, marketing manager, business partner, agency, VA) with their own logins and tailored access.'
  },
  {
    key: 'industry_benchmarking',
    title: '8. Industry benchmarking',
    description: 'Anonymously compare your store\'s performance to similar WooCommerce stores in your category.'
  }
];

const getPaymentFeatureFieldName = (featureKey) => `payment_feature_${featureKey}`;

const getInitialFormData = () => ({
  registration_rating: '',
  plugin_installation_rating: '',
  onboarding_confusion_details: '',
  wait_feeling: '',
  sensible_answer_rate: '',
  chart_usefulness_rating: '',
  sql_review_outcome: '',
  answer_speed_rating: '',
  known_wrong_answer_details: '',
  failed_question_details: '',
  top_three_questions: '',
  encountered_bugs: '',
  bug_details: '',
  comparison_rating: '',
  polished_usage_likelihood_rating: '',
  payment_feature_conversational_context: '',
  payment_feature_automated_metric_analysis: '',
  payment_feature_additional_plugins: '',
  payment_feature_more_than_10_questions_per_month: '',
  payment_feature_scheduled_reports_and_alerts: '',
  payment_feature_forecasting_and_predictive_insights: '',
  payment_feature_team_access_with_permissions: '',
  payment_feature_industry_benchmarking: '',
  other_features: '',
  monthly_value_amount_usd: '20',
  final_thoughts: ''
});

const toOptionalText = (value) => {
  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : null;
};

const decodeBase64Url = (value) => {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddedValue = normalizedValue.padEnd(Math.ceil(normalizedValue.length / 4) * 4, '=');

  try {
    return window.atob(paddedValue);
  } catch (error) {
    return '';
  }
};

const parseJwtPayload = (token) => {
  if (!token) {
    return null;
  }

  const tokenParts = token.split('.');

  if (tokenParts.length < 2) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64Url(tokenParts[1]));
  } catch (error) {
    return null;
  }
};

const buildPayload = ({ userId, formData }) => ({
    submitted_at: new Date().toISOString(),
    tester: {
      user_id: userId
    },
    onboarding: {
      registration_rating: formData.registration_rating,
      plugin_installation_rating: formData.plugin_installation_rating,
      onboarding_confusion_details: toOptionalText(formData.onboarding_confusion_details)
    },
    analysis_wait: {
      wait_feeling: formData.wait_feeling
    },
    answers: {
      sensible_answer_rate: formData.sensible_answer_rate,
      chart_usefulness_rating: formData.chart_usefulness_rating,
      sql_review_outcome: formData.sql_review_outcome,
      answer_speed_rating: formData.answer_speed_rating,
      known_wrong_answer_details: toOptionalText(formData.known_wrong_answer_details),
      failed_question_details: toOptionalText(formData.failed_question_details)
    },
    highest_value_questions: {
      top_three_questions: formData.top_three_questions.trim()
    },
    bugs: {
      encountered_bugs: formData.encountered_bugs === 'yes',
      details: formData.encountered_bugs === 'yes' ? toOptionalText(formData.bug_details) : null
    },
    value: {
      comparison_rating: formData.comparison_rating,
      polished_usage_likelihood_rating: formData.polished_usage_likelihood_rating,
      payment_likelihood_features: PAYMENT_LIKELIHOOD_FEATURES.reduce((accumulator, feature) => ({
        ...accumulator,
        [feature.key]: formData[getPaymentFeatureFieldName(feature.key)]
      }), {}),
      other_features: toOptionalText(formData.other_features),
      monthly_value_amount_usd: Number(formData.monthly_value_amount_usd),
      payment_likelihood_scale: {
        min_label: '1 - not really',
        max_label: '5 - very much so'
      }
    },
    final_thoughts: toOptionalText(formData.final_thoughts)
  }
);

const FeedbackRadioGroup = ({
  name,
  label,
  options,
  value,
  error,
  onChange,
  hint,
  required = false,
  scaleLayout = false,
  inlineLayout = false,
  stackedLayout = false
}) => (
  <div className="feedback-question">
    <FormLabel className="mb-8">
      {label}
      {required && <span className="text-color-primary"> *</span>}
    </FormLabel>
    {hint && (
      <p className="mt-0 mb-12 text-xs text-color-low">
        {hint}
      </p>
    )}
    <div className={classNames(
      'feedback-radio-group',
      scaleLayout && 'feedback-radio-group-scale',
      inlineLayout && 'feedback-radio-group-inline',
      stackedLayout && 'feedback-radio-group-stacked'
    )}>
      {options.map((option) => (
        <Radio
          key={`${name}-${option.value}`}
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={onChange}
        >
          {scaleLayout ? (
            <span className="feedback-scale-option">
              <span className="feedback-scale-option-value">{option.value}</span>
              <span className="feedback-scale-option-label">{option.label}</span>
            </span>
          ) : (
            option.label
          )}
        </Radio>
      ))}
    </div>
    {error && <FormHint status="error">{error}</FormHint>}
  </div>
);

const FeedbackTextarea = ({
  id,
  name,
  label,
  value,
  onChange,
  error,
  rows = 4,
  required = false,
  prominent = false,
  placeholder
}) => (
  <div className="feedback-question">
    <Input
      id={id}
      type="textarea"
      name={name}
      label={
        required ? `${label} *` : label
      }
      value={value}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder}
      className={prominent ? 'feedback-prominent-textarea' : ''}
      hint={error}
      status={error ? 'error' : ''}
    />
  </div>
);

const FeatureLikelihoodGrid = ({
  features,
  valueByFieldName,
  onChange,
  error
}) => (
  <div className="feedback-question">
    <FormLabel className="mb-8">
      16. Which of these features would make it more likely for you to pay for this plugin? *
    </FormLabel>
    <div className="feedback-grid-wrap">
      <div className="feedback-grid">
        <div className="feedback-grid-cell feedback-grid-heading feedback-grid-feature-heading">
          Feature
        </div>
        {PAYMENT_LIKELIHOOD_SCALE_OPTIONS.map((option) => (
          <div key={`heading-${option.value}`} className="feedback-grid-cell feedback-grid-heading feedback-grid-scale-heading">
            <span className="feedback-grid-scale-label">
              <span className="feedback-grid-scale-value">{option.label}</span>
              {(option.value === '1' || option.value === '5') && (
                <span className="feedback-grid-scale-text">
                  {option.value === '1' ? 'not really' : 'very much so'}
                </span>
              )}
            </span>
          </div>
        ))}

        {features.map((feature) => {
          const fieldName = getPaymentFeatureFieldName(feature.key);

          return (
            <React.Fragment key={feature.key}>
              <div className="feedback-grid-cell feedback-grid-feature-cell">
                <span>{feature.title}</span>
                <span
                  className="feedback-info-icon"
                  tabIndex="0"
                  aria-label={feature.description}
                  title={feature.description}
                >
                  i
                </span>
              </div>
              {PAYMENT_LIKELIHOOD_SCALE_OPTIONS.map((option) => (
                <div key={`${feature.key}-${option.value}`} className="feedback-grid-cell feedback-grid-radio-cell">
                  <Radio
                    className="feedback-grid-radio"
                    name={fieldName}
                    value={option.value}
                    checked={valueByFieldName[fieldName] === option.value}
                    onChange={onChange}
                  >
                    <span className="screen-reader">
                      {feature.title} - {option.value}
                    </span>
                  </Radio>
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
    {error && <FormHint status="error">{error}</FormHint>}
  </div>
);

const QvFeedbackForm = (props) => {
  const { auth, token, user } = useContext(AuthContext);
  const [formData, setFormData] = useState(getInitialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [pageState, setPageState] = useState(PAGE_STATES.ready);

  const jwtPayload = useMemo(() => parseJwtPayload(token), [token]);

  const userId = useMemo(() => {
    if (user && user.id) {
      return String(user.id);
    }

    if (user && user.user_id) {
      return String(user.user_id);
    }

    if (auth && auth.user && auth.user.id) {
      return String(auth.user.id);
    }

    if (auth && auth.user && auth.user.user_id) {
      return String(auth.user.user_id);
    }

    if (auth && auth.id) {
      return String(auth.id);
    }

    if (auth && auth.user_id) {
      return String(auth.user_id);
    }

    if (jwtPayload && jwtPayload.user_id) {
      return String(jwtPayload.user_id);
    }

    if (jwtPayload && jwtPayload.id) {
      return String(jwtPayload.id);
    }

    if (jwtPayload && jwtPayload.sub) {
      return String(jwtPayload.sub);
    }

    return '';
  }, [auth, jwtPayload, user]);

  const {
    className,
    topOuterDivider,
    bottomOuterDivider,
    topDivider,
    bottomDivider,
    hasBgColor,
    invertColor
  } = props;

  const outerClasses = classNames(
    'signin section',
    topOuterDivider && 'has-top-divider',
    bottomOuterDivider && 'has-bottom-divider',
    hasBgColor && 'has-bg-color',
    invertColor && 'invert-color',
    className
  );

  const innerClasses = classNames(
    'signin-inner section-inner',
    topDivider && 'has-top-divider',
    bottomDivider && 'has-bottom-divider'
  );

  const sectionHeader = {
    title: 'QV feedback form',
    paragraph: 'Please complete this short form at the end of your test session. Your answers help us improve Exanta AI before wider rollout.'
  };

  const updateField = (event) => {
    const { name, value } = event.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));

    setFieldErrors((prevState) => ({
      ...prevState,
      [name]: '',
      user_id: ''
    }));
    setSubmitError('');
  };

  const validateForm = () => {
    const errors = {};

    if (!userId) {
      errors.user_id = 'We could not load your signed-in user ID. Please log out and back in before submitting feedback.';
    }

    if (!formData.registration_rating) {
      errors.registration_rating = 'Please rate the registration process.';
    }

    if (!formData.plugin_installation_rating) {
      errors.plugin_installation_rating = 'Please rate the plugin installation process.';
    }

    if (!formData.wait_feeling) {
      errors.wait_feeling = 'Please tell us how the analysis wait felt.';
    }

    if (!formData.sensible_answer_rate) {
      errors.sensible_answer_rate = 'Please estimate how many questions returned sensible answers.';
    }

    if (!formData.chart_usefulness_rating) {
      errors.chart_usefulness_rating = 'Please rate the charts.';
    }

    if (!formData.sql_review_outcome) {
      errors.sql_review_outcome = 'Please choose an option for SQL review.';
    }

    if (!formData.answer_speed_rating) {
      errors.answer_speed_rating = 'Please rate answer speed.';
    }

    if (!formData.top_three_questions.trim()) {
      errors.top_three_questions = 'Please paste your 3 most useful questions.';
    }

    if (!formData.encountered_bugs) {
      errors.encountered_bugs = 'Please tell us whether you encountered any bugs.';
    }

    const hasMissingPaymentFeatureRatings = PAYMENT_LIKELIHOOD_FEATURES.some((feature) => (
      !formData[getPaymentFeatureFieldName(feature.key)]
    ));

    if (hasMissingPaymentFeatureRatings) {
      errors.payment_likelihood_features = 'Please rate each feature from 1 to 5.';
    }

    if (!formData.comparison_rating) {
      errors.comparison_rating = 'Please compare Exanta AI with your current approach.';
    }

    if (!formData.polished_usage_likelihood_rating) {
      errors.polished_usage_likelihood_rating = 'Please rate how likely you would be to keep using Exanta AI.';
    }

    if (!formData.monthly_value_amount_usd) {
      errors.monthly_value_amount_usd = 'Please choose a monthly value.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitError('Please answer the required questions before submitting.');
      return;
    }

    setPageState(PAGE_STATES.submitting);
    setSubmitError('');

    try {
      const payload = buildPayload({ userId, formData });
      console.log('QV feedback payload', payload);
      await submitQvFeedback({ token, payload });
      setPageState(PAGE_STATES.success);
    } catch (error) {
      setPageState(PAGE_STATES.ready);
      setSubmitError(error.message || 'Unable to submit your feedback right now. Please try again.');
    }
  };

  const renderSuccessState = () => (
    <React.Fragment>
      <SectionHeader
        tag="h1"
        data={{
          title: 'Thank you for your feedback',
          paragraph: 'Your QV responses have been submitted successfully. We really appreciate the time you spent testing Exanta AI.'
        }}
        className="center-content"
      />
      <div className="tiles-wrap">
        <div className="tiles-item feedback-tiles-item">
          <div className="tiles-item-inner has-shadow center-content">
            <p className="mt-0 mb-24 text-color-low">
              If you want to continue exploring the product, you can return to your account area below.
            </p>
            <Button tag={Link} to="/account" color="primary" wide>
              Back to Account
            </Button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  return (
    <section className={outerClasses}>
      <div className="container">
        <div className={innerClasses}>
          {pageState === PAGE_STATES.success ? (
            renderSuccessState()
          ) : (
            <React.Fragment>
              <SectionHeader tag="h1" data={sectionHeader} className="center-content" />
              <div className="tiles-wrap">
                <div className="tiles-item feedback-tiles-item">
                  <div className="tiles-item-inner has-shadow">
                    <form onSubmit={handleSubmit} noValidate>
                      <input type="hidden" name="user_id" value={userId} />
                      <fieldset>
                        <div className="feedback-form-section">
                          <h3 className="mt-0 mb-24">Section 1 - Onboarding</h3>
                          {fieldErrors.user_id && (
                            <div className="mb-16 text-sm text-color-error">
                              {fieldErrors.user_id}
                            </div>
                          )}

                          <FeedbackRadioGroup
                            name="registration_rating"
                            label="1. How would you rate the registration process at exanta.ai?"
                            options={REGISTRATION_OPTIONS}
                            value={formData.registration_rating}
                            error={fieldErrors.registration_rating}
                            onChange={updateField}
                            required
                            scaleLayout
                          />

                          <FeedbackRadioGroup
                            name="plugin_installation_rating"
                            label="2. How would you rate the plugin installation process in WordPress?"
                            options={INSTALLATION_OPTIONS}
                            value={formData.plugin_installation_rating}
                            error={fieldErrors.plugin_installation_rating}
                            onChange={updateField}
                            required
                            scaleLayout
                          />

                          <FeedbackTextarea
                            id="onboarding_confusion_details"
                            name="onboarding_confusion_details"
                            label="3. If anything about registration or installation was confusing, please describe it."
                            value={formData.onboarding_confusion_details}
                            onChange={updateField}
                            error={fieldErrors.onboarding_confusion_details}
                            rows={4}
                          />
                        </div>

                        <div className="feedback-form-section">
                          <h3 className="mt-0 mb-24">Section 2 - Database analysis wait</h3>
                          <FeedbackRadioGroup
                            name="wait_feeling"
                            label={'4. While "Analyse Database Structure" was running, how did the wait feel?'}
                            options={WAIT_FEELING_OPTIONS}
                            value={formData.wait_feeling}
                            error={fieldErrors.wait_feeling}
                            onChange={updateField}
                            required
                            scaleLayout
                          />
                        </div>

                        <div className="feedback-form-section">
                          <h3 className="mt-0 mb-24">Section 3 - Asking questions and getting answers</h3>
                          <FeedbackRadioGroup
                            name="sensible_answer_rate"
                            label="5. Of the questions you asked, roughly how many returned a sensible answer?"
                            options={SENSIBLE_ANSWER_OPTIONS}
                            value={formData.sensible_answer_rate}
                            error={fieldErrors.sensible_answer_rate}
                            onChange={updateField}
                            required
                            inlineLayout
                          />

                          <FeedbackRadioGroup
                            name="chart_usefulness_rating"
                            label="6. Were the charts useful and clear?"
                            options={CHART_USEFULNESS_OPTIONS}
                            value={formData.chart_usefulness_rating}
                            error={fieldErrors.chart_usefulness_rating}
                            onChange={updateField}
                            required
                            scaleLayout
                          />

                          <FeedbackRadioGroup
                            name="sql_review_outcome"
                            label="7. When you reviewed the SQL, did it look correct?"
                            options={SQL_REVIEW_OPTIONS}
                            value={formData.sql_review_outcome}
                            error={fieldErrors.sql_review_outcome}
                            onChange={updateField}
                            required
                            stackedLayout
                          />

                          <FeedbackRadioGroup
                            name="answer_speed_rating"
                            label="8. How fast did the answers feel?"
                            options={ANSWER_SPEED_OPTIONS}
                            value={formData.answer_speed_rating}
                            error={fieldErrors.answer_speed_rating}
                            onChange={updateField}
                            required
                            scaleLayout
                          />

                          <FeedbackTextarea
                            id="known_wrong_answer_details"
                            name="known_wrong_answer_details"
                            label="9. Did any question return an answer you knew was wrong? Paste the question and what was wrong."
                            value={formData.known_wrong_answer_details}
                            onChange={updateField}
                            error={fieldErrors.known_wrong_answer_details}
                            rows={5}
                          />

                          <FeedbackTextarea
                            id="failed_question_details"
                            name="failed_question_details"
                            label="10. Did any question fail outright? Paste the question."
                            value={formData.failed_question_details}
                            onChange={updateField}
                            error={fieldErrors.failed_question_details}
                            rows={4}
                          />
                        </div>

                        <div className="feedback-form-section">
                          <h3 className="mt-0 mb-24">Section 4 - Your highest-value question</h3>
                          <FeedbackTextarea
                            id="top_three_questions"
                            name="top_three_questions"
                            label="11. Paste the 3 most useful questions you asked - the ones you would want to ask regularly."
                            value={formData.top_three_questions}
                            onChange={updateField}
                            error={fieldErrors.top_three_questions}
                            rows={7}
                            required
                            prominent
                            placeholder={'1. \n2. \n3. '}
                          />
                        </div>

                        <div className="feedback-form-section">
                          <h3 className="mt-0 mb-24">Section 5 - Bugs</h3>
                          <FeedbackRadioGroup
                            name="encountered_bugs"
                            label="12. Did you encounter any bugs?"
                            options={YES_NO_OPTIONS}
                            value={formData.encountered_bugs}
                            error={fieldErrors.encountered_bugs}
                            onChange={updateField}
                            required
                          />

                          {formData.encountered_bugs === 'yes' && (
                            <React.Fragment>
                              <FeedbackTextarea
                                id="bug_details"
                                name="bug_details"
                                label="13. If yes, describe what happened. Screenshots welcome."
                                value={formData.bug_details}
                                onChange={updateField}
                                error={fieldErrors.bug_details}
                                rows={5}
                              />
                            </React.Fragment>
                          )}
                        </div>

                        <div className="feedback-form-section">
                          <h3 className="mt-0 mb-24">Section 6 - Overall experience and value</h3>
                          <FeedbackRadioGroup
                            name="comparison_rating"
                            label="14. Compared to how you currently get answers about your store data, how does Exanta AI compare?"
                            options={COMPARISON_OPTIONS}
                            value={formData.comparison_rating}
                            error={fieldErrors.comparison_rating}
                            onChange={updateField}
                            required
                            scaleLayout
                          />

                          <FeedbackRadioGroup
                            name="polished_usage_likelihood_rating"
                            label="15. If Exanta AI were polished and reliable, how likely would you be to keep using it?"
                            options={LIKELIHOOD_OPTIONS}
                            value={formData.polished_usage_likelihood_rating}
                            error={fieldErrors.polished_usage_likelihood_rating}
                            onChange={updateField}
                            required
                            scaleLayout
                          />

                          <FeatureLikelihoodGrid
                            features={PAYMENT_LIKELIHOOD_FEATURES}
                            valueByFieldName={formData}
                            onChange={updateField}
                            error={fieldErrors.payment_likelihood_features}
                          />

                          <div className="feedback-question">
                            <FormLabel id="monthly_value_amount_usd">
                              17. Assuming that 4 of the above features were implemented how much would a tool like this be worth to you per month? *
                            </FormLabel>
                            <div className="feedback-slider-value">
                              ${formData.monthly_value_amount_usd} / month
                            </div>
                            <input
                              id="monthly_value_amount_usd"
                              type="range"
                              name="monthly_value_amount_usd"
                              min="0"
                              max="100"
                              step="2"
                              value={formData.monthly_value_amount_usd}
                              onChange={updateField}
                              className="feedback-range-input"
                            />
                            <div className="feedback-slider-scale text-xs text-color-low">
                              <span>$0</span>
                              <span>$100</span>
                            </div>
                            {fieldErrors.monthly_value_amount_usd && (
                              <FormHint status="error">{fieldErrors.monthly_value_amount_usd}</FormHint>
                            )}
                          </div>

                          <FeedbackTextarea
                            id="other_features"
                            name="other_features"
                            label="18. What other feature(s) would make this plugin useful to you?"
                            value={formData.other_features}
                            onChange={updateField}
                            error={fieldErrors.other_features}
                            rows={5}
                          />
                        </div>

                        <div className="feedback-form-section feedback-form-section-last">
                          <h3 className="mt-0 mb-24">Section 7 - Final thoughts</h3>
                          <FeedbackTextarea
                            id="final_thoughts"
                            name="final_thoughts"
                            label="19. Anything else worth knowing?"
                            value={formData.final_thoughts}
                            onChange={updateField}
                            error={fieldErrors.final_thoughts}
                            rows={4}
                          />
                        </div>

                        {submitError && (
                          <div className="mb-24 text-sm text-color-error">
                            {submitError}
                          </div>
                        )}

                        <div className="feedback-form-actions">
                          <Button tag={Link} to="/account" color="dark" wideMobile>
                            Back to Account
                          </Button>
                          <Button
                            color="primary"
                            type="submit"
                            loading={pageState === PAGE_STATES.submitting}
                            disabled={pageState === PAGE_STATES.submitting}
                          >
                            Submit Feedback
                          </Button>
                        </div>
                      </fieldset>
                    </form>
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    </section>
  );
};

QvFeedbackForm.propTypes = propTypes;
QvFeedbackForm.defaultProps = defaultProps;

export default QvFeedbackForm;
